import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Lock } from "lucide-react";

// Paths that render course-scoped content. When the student is on one of
// these URLs and their enrollment is revoked/suspended, we auto-navigate
// them back to /student/enrollments so they can't keep interacting with
// stale in-course UI.
const COURSE_CONTENT_PATTERNS = [
  /^\/student\/courses\/[^/]+\/subjects/,
  /^\/student\/subjects\//,
  /^\/student\/modules\//,
  /^\/student\/smart-review/,
  /^\/student\/mock-exam/,
  /^\/student\/abstract-exam/,
];

function isOnCourseContentPage(pathname) {
  return COURSE_CONTENT_PATTERNS.some((re) => re.test(pathname || ""));
}

/**
 * SuspendedAccessDialog — global listener for `pm-enrollment-suspended`
 * events. Fires from two sources:
 *   1. The axios interceptor when the server returns 423/403 (reactive)
 *   2. The student-session WebSocket when admin suspends/revokes the course
 *      while the student is logged in (proactive — near-instant push)
 *
 * When triggered, we always show the dialog. If the student is currently
 * inside course-scoped content (e.g. a module or mock-exam), we also
 * immediately navigate them to /student/enrollments so they can't keep
 * interacting with the revoked course.
 */
export default function SuspendedAccessDialog() {
  const [payload, setPayload] = useState(null); // { code, course_id, course_name, reason }
  const nav = useNavigate();

  useEffect(() => {
    const onSuspended = (e) => {
      const d = e?.detail || {};
      setPayload({
        code: d.code || "enrollment_suspended",
        course_id: d.course_id,
        course_name: d.course_name || "this course",
        reason: d.reason || "",
      });
      // Refresh cached user so enrolled_course_ids drops the revoked course.
      api.get("/auth/me").then((r) => {
        try { localStorage.setItem("pm_user", JSON.stringify(r.data)); } catch { /* noop */ }
      }).catch(() => { /* interceptor handles */ });
      // If the student is inside course content right now, boot them out
      // immediately — the dialog will still be on top of /student/enrollments.
      if (isOnCourseContentPage(window.location.pathname)) {
        nav("/student/enrollments", { replace: true });
      }
    };
    window.addEventListener("pm-enrollment-suspended", onSuspended);
    return () => window.removeEventListener("pm-enrollment-suspended", onSuspended);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToEnrollments = () => {
    setPayload(null);
    nav("/student/enrollments");
  };

  const goToSupport = () => {
    setPayload(null);
    nav("/student/support");
  };

  const isDeleted = payload?.code === "not_enrolled";
  const title = isDeleted
    ? `Your enrollment in ${payload?.course_name} was revoked`
    : `Access to ${payload?.course_name} has been suspended`;

  return (
    <AlertDialog open={!!payload} onOpenChange={(o) => !o && setPayload(null)}>
      <AlertDialogContent data-testid="suspended-access-dialog">
        <AlertDialogHeader>
          <div className="mx-auto h-12 w-12 rounded-2xl bg-amber-100 grid place-items-center mb-2">
            <Lock className="w-6 h-6 text-amber-600" />
          </div>
          <AlertDialogTitle className="text-center">
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-center space-y-2">
              {!isDeleted && payload?.reason && (
                <div className="text-sm text-slate-800 bg-amber-50 border border-amber-200 rounded p-3 text-left">
                  <span className="font-semibold">Reason:</span> {payload.reason}
                </div>
              )}
              {isDeleted && (
                <div className="text-sm text-slate-700 pt-1">
                  Your enrollment was revoked by an administrator.
                </div>
              )}
              <p className="text-sm text-slate-600 pt-2">
                Please{" "}
                <button
                  type="button"
                  onClick={goToSupport}
                  className="underline text-blue-700 hover:text-blue-800 font-semibold"
                  data-testid="suspended-contact-support"
                >
                  contact support
                </button>{" "}
                {isDeleted ? "to re-enroll." : "to restore your access."}
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="suspended-close">Close</AlertDialogCancel>
          <AlertDialogAction
            onClick={goToEnrollments}
            data-testid="suspended-go-to-enrollments"
          >
            Go to My Enrollments
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
